/**
 * useWhatsApp - Hook for WhatsApp inbox integration
 * Manages connection status, conversations, and customer matching
 */

import { useState, useCallback } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../lib/api';
import type {
  WhatsAppConversation,
  WhatsAppStatusResponse,
  WhatsAppQRResponse,
  CustomerByPhoneResult,
} from '@leal-crm/shared';

interface CustomerMatch {
  status: 'matched' | 'multiple' | 'not_found';
  customer?: {
    id: string;
    name: string;
    email?: string;
    phone: string;
    phoneNormalized?: string;
  };
  customers?: Array<{
    id: string;
    name: string;
    email?: string;
    phone: string;
  }>;
  vehicles?: Array<{
    id: string;
    plate: string;
    brand: string;
    model: string;
    year?: number;
  }>;
  activeBookings?: Array<{
    id: string;
    reference: string;
    type: string;
    status: string;
    createdAt: string;
  }>;
  matchedOn: string;
}

export function useWhatsApp() {
  const [selectedConversation, setSelectedConversation] =
    useState<WhatsAppConversation | null>(null);

  // Fetch connection status (poll every 5s)
  const {
    data: statusData,
    isLoading: isLoadingStatus,
  } = useQuery({
    queryKey: ['whatsapp', 'status'],
    queryFn: () => api.get<WhatsAppStatusResponse>('/whatsapp/status'),
    refetchInterval: 5000,
  });

  const status = statusData?.status || 'disconnected';

  // Fetch QR code when status is qr_pending or disconnected
  const {
    data: qrData,
    isLoading: isLoadingQR,
    refetch: refreshQR,
  } = useQuery({
    queryKey: ['whatsapp', 'qr'],
    queryFn: () => api.get<WhatsAppQRResponse>('/whatsapp/qr'),
    enabled: status === 'qr_pending' || status === 'disconnected',
    refetchInterval: status === 'qr_pending' ? 3000 : false,
  });

  // Fetch conversations when ready
  const {
    data: conversationsData,
    isLoading: isLoadingConversations,
  } = useQuery({
    queryKey: ['whatsapp', 'conversations'],
    queryFn: () =>
      api.get<{ conversations: WhatsAppConversation[] }>(
        '/whatsapp/conversations'
      ),
    enabled: status === 'ready',
    refetchInterval: 10000,
  });

  // Match customer by phone
  const matchMutation = useMutation({
    mutationFn: (phone: string) =>
      api.post<CustomerMatch>('/whatsapp/match', { phone }),
  });

  // When a conversation is selected, try to match the customer
  const selectConversation = useCallback(
    (conversation: WhatsAppConversation) => {
      setSelectedConversation(conversation);
      matchMutation.mutate(conversation.contactPhone);
    },
    [matchMutation]
  );

  // Also try to load customer data via by-phone endpoint for richer data
  const {
    data: customerByPhoneData,
  } = useQuery({
    queryKey: ['customer', 'by-phone', selectedConversation?.contactPhone],
    queryFn: () =>
      api.get<CustomerByPhoneResult>(
        `/customers/by-phone?phone=${encodeURIComponent(selectedConversation?.contactPhone || '')}`
      ),
    enabled: !!selectedConversation?.contactPhone && status === 'ready',
  });

  // Build customer match result combining both sources
  const customerMatch: CustomerMatch | null = (() => {
    // Prefer by-phone endpoint data (richer)
    if (customerByPhoneData?.customer) {
      return {
        status: 'matched' as const,
        customer: customerByPhoneData.customer,
        vehicles: customerByPhoneData.vehicles,
        activeBookings: customerByPhoneData.activeBookings,
        matchedOn: 'phone_normalized',
      };
    }
    // Fallback to match endpoint
    if (matchMutation.data) {
      return matchMutation.data;
    }
    // No match yet
    if (selectedConversation && !matchMutation.isPending) {
      return { status: 'not_found' as const, matchedOn: 'phone_normalized' };
    }
    return null;
  })();

  return {
    status,
    qrDataUrl: qrData?.qr || null,
    conversations: conversationsData?.conversations || [],
    isLoadingStatus,
    isLoadingConversations,
    isLoadingQR,
    customerMatch,
    isLoadingMatch: matchMutation.isPending,
    refreshQR: () => { refreshQR(); },
    selectConversation,
    selectedConversation,
  };
}
